import { isEncryptedField } from '@/infra/crypto/payloads'
import type { EncryptionStatus } from '@/features/events/shared/types/EncryptionInfo'
import type { DecryptFn } from '@/infra/sse/shared/decryption'
import {
  ENCRYPTED_PLACEHOLDER,
  asRecordOrNull,
  asStringOrFallback,
  processRawMetadata,
  tryDecryptRecord,
  tryDecryptString,
} from '@/infra/sse/shared/decryption'
import type { MetadataPayload, Telemetry, TelemetryPayload } from '@/infra/sse/shared/payloads'
import type { ConversationEventPayload } from '..'
import type { RawConversationEventPayload } from './types'
import { resolveNotificationContext } from './resolveNotificationContext'
import { buildFileChanges, getFileName } from './buildFileChanges'

const CONTINUE_SESSION_MESSAGE = 'Continue your Claude Code session'
const FILE_MODIFICATION_PREFIX = 'Claude Code used tool'

function resolveEncryptionStatus(raw: RawConversationEventPayload): EncryptionStatus {
  return raw.encryption
    ? { encrypted: true, algorithm: 'AES-256-GCM' as const, keyVersion: raw.encryption.keyVersion }
    : null
}

export const processRawConversationEvent = async (
  raw: RawConversationEventPayload,
  decrypt: DecryptFn | null,
): Promise<ConversationEventPayload> => {
  const keyVersion = raw.encryption?.keyVersion
  const metadata = await processRawMetadata(raw.eventMetadata, decrypt, keyVersion)

  switch (raw.eventType) {
    case 'NOTIFICATION':
      return processNotification(raw, decrypt, keyVersion, metadata)
    case 'AGENT_REPLY':
      return processAgentReply(raw, decrypt, keyVersion, metadata)
    case 'AGENT_NARRATIVE':
      return processAgentNarrative(raw, decrypt, keyVersion, metadata)
    case 'FILE_MODIFICATION':
      return processFileModification(raw, decrypt, keyVersion, metadata)
    case 'USER_PROMPT':
      return processUserPrompt(raw, decrypt, keyVersion, metadata)
    default:
      return buildBasePayload(raw, metadata)
  }
}

function toTelemetryPayload(telemetry: Telemetry): TelemetryPayload {
  if (telemetry.tmuxSession) {
    return { tmuxSession: telemetry.tmuxSession }
  }
  return {}
}

async function processNotification(
  raw: RawConversationEventPayload,
  decrypt: DecryptFn | null,
  keyVersion: number | undefined,
  metadata: MetadataPayload,
): Promise<ConversationEventPayload> {
  const notificationMessage =
    raw.rawHookPayload !== null && typeof raw.rawHookPayload['message'] === 'string'
      ? raw.rawHookPayload['message']
      : null

  const decryptedContext =
    decrypt !== null && keyVersion !== undefined && isEncryptedField(raw.rawNotificationContext)
      ? await tryDecryptRecord(raw.rawNotificationContext, keyVersion, decrypt)
      : asRecordOrNull(raw.rawNotificationContext)

  const resolution = resolveNotificationContext(decryptedContext)

  return {
    notificationMessage,
    toolName: resolution.toolName,
    notificationContext: resolution.notificationContext,
    agentResponse: null,
    fileChanges: null,
    userPrompt: null,
    eventType: resolution.eventType,
    eventMetadata: metadata,
    hostDetails: raw.hostDetails,
    encryption: resolveEncryptionStatus(raw),
    telemetry: toTelemetryPayload(raw.telemetry),
  }
}

async function decryptAgentResponse(
  raw: RawConversationEventPayload,
  decrypt: DecryptFn | null,
  keyVersion: number | undefined,
): Promise<string | null> {
  if (decrypt !== null && keyVersion !== undefined && isEncryptedField(raw.agentResponse)) {
    return tryDecryptString(raw.agentResponse, keyVersion, decrypt, ENCRYPTED_PLACEHOLDER)
  }
  const fallback = asStringOrFallback(raw.agentResponse, ENCRYPTED_PLACEHOLDER)
  return fallback === ENCRYPTED_PLACEHOLDER && raw.agentResponse === null ? null : fallback
}

async function processAgentReply(
  raw: RawConversationEventPayload,
  decrypt: DecryptFn | null,
  keyVersion: number | undefined,
  metadata: MetadataPayload,
): Promise<ConversationEventPayload> {
  return {
    notificationMessage: CONTINUE_SESSION_MESSAGE,
    toolName: null,
    notificationContext: null,
    agentResponse: await decryptAgentResponse(raw, decrypt, keyVersion),
    fileChanges: null,
    userPrompt: null,
    eventType: raw.eventType,
    eventMetadata: metadata,
    hostDetails: raw.hostDetails,
    encryption: resolveEncryptionStatus(raw),
    telemetry: toTelemetryPayload(raw.telemetry),
  }
}

async function processAgentNarrative(
  raw: RawConversationEventPayload,
  decrypt: DecryptFn | null,
  keyVersion: number | undefined,
  metadata: MetadataPayload,
): Promise<ConversationEventPayload> {
  return {
    notificationMessage: null,
    toolName: null,
    notificationContext: null,
    agentResponse: await decryptAgentResponse(raw, decrypt, keyVersion),
    fileChanges: null,
    userPrompt: null,
    eventType: raw.eventType,
    eventMetadata: metadata,
    hostDetails: raw.hostDetails,
    encryption: resolveEncryptionStatus(raw),
    telemetry: toTelemetryPayload(raw.telemetry),
  }
}

async function processFileModification(
  raw: RawConversationEventPayload,
  decrypt: DecryptFn | null,
  keyVersion: number | undefined,
  metadata: MetadataPayload,
): Promise<ConversationEventPayload> {
  const toolName =
    raw.rawHookPayload !== null && typeof raw.rawHookPayload['tool_name'] === 'string'
      ? raw.rawHookPayload['tool_name']
      : null

  let toolInput: Record<string, unknown> | null = null
  let toolResponse: Record<string, unknown> | null = null

  if (raw.rawHookPayload !== null) {
    const rawToolInput = raw.rawHookPayload['tool_input']
    if (decrypt !== null && keyVersion !== undefined && isEncryptedField(rawToolInput)) {
      toolInput = await tryDecryptRecord(rawToolInput, keyVersion, decrypt)
    } else {
      toolInput = asRecordOrNull(rawToolInput)
    }

    const rawToolResponse = raw.rawHookPayload['tool_response']
    if (decrypt !== null && keyVersion !== undefined && isEncryptedField(rawToolResponse)) {
      toolResponse = await tryDecryptRecord(rawToolResponse, keyVersion, decrypt)
    } else {
      toolResponse = asRecordOrNull(rawToolResponse)
    }
  }

  let fileChanges = null
  let notificationMessage: string | null = null

  if (toolName && toolInput && toolResponse) {
    fileChanges = buildFileChanges(toolName, toolInput, toolResponse)
    if (fileChanges) {
      const fileName = getFileName(fileChanges.targetPath)
      notificationMessage = `${FILE_MODIFICATION_PREFIX} ${toolName}: ${fileName}`
    }
  }

  return {
    notificationMessage,
    toolName,
    notificationContext: null,
    agentResponse: null,
    fileChanges,
    userPrompt: null,
    eventType: raw.eventType,
    eventMetadata: metadata,
    hostDetails: raw.hostDetails,
    encryption: resolveEncryptionStatus(raw),
    telemetry: toTelemetryPayload(raw.telemetry),
  }
}

async function processUserPrompt(
  raw: RawConversationEventPayload,
  decrypt: DecryptFn | null,
  keyVersion: number | undefined,
  metadata: MetadataPayload,
): Promise<ConversationEventPayload> {
  let userPrompt: string | null = null

  if (raw.rawHookPayload !== null) {
    const rawPrompt = raw.rawHookPayload['prompt']
    if (decrypt !== null && keyVersion !== undefined && isEncryptedField(rawPrompt)) {
      userPrompt = await tryDecryptString(rawPrompt, keyVersion, decrypt, ENCRYPTED_PLACEHOLDER)
    } else {
      userPrompt = typeof rawPrompt === 'string' ? rawPrompt : null
    }
  }

  return {
    notificationMessage: null,
    toolName: null,
    notificationContext: null,
    agentResponse: null,
    fileChanges: null,
    userPrompt,
    eventType: raw.eventType,
    eventMetadata: metadata,
    hostDetails: raw.hostDetails,
    encryption: resolveEncryptionStatus(raw),
    telemetry: toTelemetryPayload(raw.telemetry),
  }
}

function buildBasePayload(
  raw: RawConversationEventPayload,
  metadata: MetadataPayload,
): ConversationEventPayload {
  return {
    notificationMessage: null,
    toolName: null,
    notificationContext: null,
    agentResponse: null,
    fileChanges: null,
    userPrompt: null,
    eventType: raw.eventType,
    eventMetadata: metadata,
    hostDetails: raw.hostDetails,
    encryption: resolveEncryptionStatus(raw),
    telemetry: toTelemetryPayload(raw.telemetry),
  }
}
